'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();

import { cn } from '@qogita/ui/utils/cn'
import { BaseButton } from '@qogita/ui/v2/base-button'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { ComponentProps, createContext, useContext, useState } from 'react'

import { Icon } from './icon'

type DrawerContextType = {
  open: boolean
  closeDrawer: () => void
}
const DrawerContext = createContext<DrawerContextType | null>(null)

/**
 * Returns the open state and an onOpenChange handler for the drawer
 * Returns no-op values if used outside of a drawer context.
 *
 * This optional context is useful for components that may or may not be in a drawer.
 * For instance navigation links that only appear in a drawer on mobile but not desktop
 */
export function useOptionalDrawerContext() {
  const context = useContext(DrawerContext)

  if (!context) {
    return {
      open: false,
      closeDrawer: () => {},
    }
  }

  return context
}

export function Drawer({
  children,
  defaultOpen = false,
  onOpenChange: onOpenChangeProperty,
  open: controlledOpen,
  ...props
}: ComponentProps<typeof DialogPrimitive.Root>) {
  const [open, setOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : open

  function handleOpenChange(isOpen: boolean) {
    if (!isControlled) {
      setOpen(isOpen)
    }
    onOpenChangeProperty?.(isOpen)
  }

  function closeDrawer() {
    handleOpenChange(false)
  }

  return (
    <DrawerContext.Provider value={{ open: isOpen, closeDrawer }}>
      <DialogPrimitive.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        {...props}
      >
        {children}
      </DialogPrimitive.Root>
    </DrawerContext.Provider>
  )
}

export function DrawerTrigger({
  asChild,
  ...props
}: ComponentProps<typeof BaseButton> & { asChild?: boolean }) {
  return (
    <DialogPrimitive.Trigger asChild>
      {asChild ? props.children : <BaseButton {...props} />}
    </DialogPrimitive.Trigger>
  )
}

export const DrawerClose = DialogPrimitive.Close

const DrawerPortal = DialogPrimitive.Portal

function DrawerOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-gray-900/40',
        className,
      )}
      {...props}
    />
  )
}

const drawerVariants = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col bg-white shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
  {
    variants: {
      side: {
        left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-full sm:max-w-sm sm:border-r',
        right:
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-full sm:max-w-sm sm:border-l',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
)

export function DrawerContent({
  side,
  className,
  children,
  // Defaults to undefined to avoid RadixUI warnings about not having provided a description for the dialog
  'aria-describedby': ariaDescribedby = undefined,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof drawerVariants>) {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        className={cn(drawerVariants({ side }), className)}
        aria-describedby={ariaDescribedby}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DrawerPortal>
  )
}

export function DrawerHeader({
  className,
  children,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 border-b border-gray-200 p-6',
        className,
      )}
      {...props}
    >
      {children}
      <DrawerClose className="ml-auto rounded-xs focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-gray-800">
        <Icon name="heroicons/solid/x-mark" className="size-6 text-gray-400" />
        <span className="sr-only">Close</span>
      </DrawerClose>
    </div>
  )
}

export function DrawerBody({
  className,
  children,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div className={cn('flex-1 overflow-y-auto p-6', className)} {...props}>
      {children}
    </div>
  )
}

export function DrawerFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('border-t border-gray-200 p-6', className)} {...props} />
  )
}

export function DrawerTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-bold text-gray-900', className)}
      {...props}
    />
  )
}
